// AvatarPicker — color + emoji selector used by both the adult
// account-edit flow and the parent's add-kid flow.
//
// Visual identity in kid mode is per-account color (OKLCH) plus a
// single emoji mascot — same shape as adult accounts. Keeping the
// picker shared means both surfaces stay consistent and parents can
// upgrade their own avatar in the same UI they use to add kids.
//
// Storage shape (`value` / `onChange`):
//   { color: string | null, emoji: string | null }
//
// Color values are full OKLCH expressions (`oklch(0.78 0.16 30)`) —
// the kid handoff uses these literally as CSS color tokens. Emoji is
// stored as the grapheme; rendered as-is.

import { Icon } from "../icons";

interface AvatarValue {
  color: string | null;
  emoji: string | null;
}

interface AvatarPickerProps {
  value: AvatarValue;
  onChange: (next: AvatarValue) => void;
  /** Bigger preview tile + grid on the parent's "Add a kid" page;
   *  compact form (just the row) when embedded in an account edit. */
  size?: "compact" | "full";
}

// 12 OKLCH colors spaced around the hue wheel. Lightness 0.78 +
// chroma 0.16 matches the kid handoff palette (the spec calls out
// Ezra=coral hue ~30, Iris=magenta hue ~320). Including a "no color"
// option for users who don't want one.
const COLOR_SWATCHES: { name: string; value: string }[] = [
  { name: "Coral",     value: "oklch(0.78 0.16 30)"  },
  { name: "Amber",     value: "oklch(0.82 0.16 70)"  },
  { name: "Yellow",    value: "oklch(0.85 0.16 100)" },
  { name: "Lime",      value: "oklch(0.82 0.16 130)" },
  { name: "Green",     value: "oklch(0.78 0.16 160)" },
  { name: "Teal",      value: "oklch(0.78 0.14 190)" },
  { name: "Cyan",      value: "oklch(0.80 0.14 220)" },
  { name: "Blue",      value: "oklch(0.72 0.16 250)" },
  { name: "Violet",    value: "oklch(0.72 0.18 280)" },
  { name: "Purple",    value: "oklch(0.72 0.18 300)" },
  { name: "Magenta",   value: "oklch(0.78 0.16 320)" },
  { name: "Pink",      value: "oklch(0.80 0.16 350)" },
];

// Kid-friendly emoji set. Curated for "looks like a mascot at small
// size" — animals, vehicles, food, nature, faces. Parents see the
// same picker so the set is mixed (adults will reach for the briefcase
// or the coffee cup).
const EMOJI_OPTIONS: string[] = [
  "🚀", "🦄", "🐱", "🐶", "🐰", "🐻", "🐼", "🐯", "🦊", "🐸",
  "🐙", "🦋", "🌸", "🌟", "⭐", "🌈", "🍎", "🍕", "🍦", "🧁",
  "🎮", "🎨", "🎵", "📚", "⚽", "🏀", "🚲", "✈️", "🚂", "🛸",
  "💎", "🌙", "☀️", "🔥", "❄️", "⚡",
  // Adult-ish picks for parents:
  "☕", "🍳", "🪴", "📖", "🎸", "🧠", "💼", "🛠️",
];

export function AvatarPicker({ value, onChange, size = "full" }: AvatarPickerProps) {
  const setColor = (color: string | null) => onChange({ ...value, color });
  const setEmoji = (emoji: string | null) => onChange({ ...value, emoji });

  // Background style for the preview tile. Falls back to a neutral
  // surface when no color is picked so the emoji is still readable.
  const previewBg = value.color
    ? { background: value.color }
    : { background: "var(--surface-2, var(--bg-elev))" };

  return (
    <div className={`avatar-picker avatar-picker-${size}`}>
      <div className="avatar-picker-preview" style={previewBg as any}>
        {value.emoji
          ? <span className="avatar-picker-emoji">{value.emoji}</span>
          : <span className="avatar-picker-placeholder"><Icon name="people" size={size === "full" ? 24 : 16} /></span>}
      </div>

      <div className="avatar-picker-controls">
        <div className="avatar-picker-section">
          <div className="avatar-picker-label">Color</div>
          <div className="avatar-picker-swatches">
            {COLOR_SWATCHES.map((c) => (
              <button
                key={c.value}
                className="avatar-picker-swatch"
                data-active={value.color === c.value}
                style={{ background: c.value }}
                onClick={() => setColor(c.value)}
                type="button"
                title={c.name}
                aria-label={`Color ${c.name}`}
              />
            ))}
            <button
              className="avatar-picker-swatch avatar-picker-swatch-clear"
              data-active={value.color === null}
              onClick={() => setColor(null)}
              type="button"
              title="No color"
              aria-label="No color"
            >
              <Icon name="x" size={10} />
            </button>
          </div>
        </div>

        <div className="avatar-picker-section">
          <div className="avatar-picker-label">Emoji</div>
          <div className="avatar-picker-emoji-grid">
            {EMOJI_OPTIONS.map((e) => (
              <button
                key={e}
                className="avatar-picker-emoji-btn"
                data-active={value.emoji === e}
                onClick={() => setEmoji(e)}
                type="button"
                aria-label={`Emoji ${e}`}
              >
                {e}
              </button>
            ))}
            <button
              className="avatar-picker-emoji-btn avatar-picker-emoji-clear"
              data-active={value.emoji === null}
              onClick={() => setEmoji(null)}
              type="button"
              title="No emoji"
              aria-label="No emoji"
            >
              <Icon name="x" size={11} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
